import type { TenantThemeDocument } from '@vipso/shared';
import type {
  Customer,
  Vendor,
  Product,
  ProcRow,
  ProcProductLine,
  DashboardStats,
  StaffRow,
  TaskRow,
  ContactRow,
  CategoryRow,
  UnitRow,
  RoleRow,
  ChamberRow,
  FloorRow,
  RackRow,
} from '../api/types';

export const DEMO_OTP = '1234';

export const DEMO_TENANT = {
  id: '1',
  name: 'VIPSo',
  slug: 'vipso',
  plan: 'enterprise',
  status: 'ACTIVE',
};

/**
 * Theme documents use the same shape as the real app's `TenantThemeDocument`
 * (persisted JSON / `initApp.theme`). In production today themes are tenant-scoped;
 * the demo attaches one per org so switching orgs shows distinct branding — ready to
 * map 1:1 when the product manages themes per org/tenant in admin.
 */
export type DemoOrgTheme = TenantThemeDocument & {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string | null;
  fontFamily: string;
  mode: string;
};

/** Default / pre-login theme (VIPSo red). */
export const DEMO_THEME: DemoOrgTheme = {
  primaryColor: '#cc202e',
  secondaryColor: '#ffdbdd',
  logoUrl: '/brand/logo-sm.svg',
  faviconUrl: null,
  fontFamily: '"Montserrat", "Segoe UI", system-ui, -apple-system, sans-serif',
  mode: 'light',
  primaryShade: 6,
  defaultRadius: 'md',
  other: {
    vipsoNavy: '#000e21',
    vipsoCanvas: '#fafafa',
    vipsoRed: '#cc202e',
    vipsoSecondary: '#ffdbdd',
  },
};

/** Acme — classic VIPSo red */
export const THEME_ACME: DemoOrgTheme = {
  ...DEMO_THEME,
  primaryColor: '#cc202e',
  secondaryColor: '#ffdbdd',
  other: {
    ...DEMO_THEME.other,
    vipsoRed: '#cc202e',
    vipsoSecondary: '#ffdbdd',
    brandLabel: 'Acme Traders',
  },
};

/** Sunrise — warm amber */
export const THEME_SUNRISE: DemoOrgTheme = {
  primaryColor: '#d97706',
  secondaryColor: '#fef3c7',
  logoUrl: '/brand/logo-sm.svg',
  faviconUrl: null,
  fontFamily: '"Montserrat", "Segoe UI", system-ui, -apple-system, sans-serif',
  mode: 'light',
  primaryShade: 6,
  defaultRadius: 'md',
  other: {
    vipsoNavy: '#1c1917',
    vipsoCanvas: '#fffbeb',
    vipsoRed: '#d97706',
    vipsoSecondary: '#fef3c7',
    brandLabel: 'Sunrise Wholesale',
  },
};

/** Metro — deep teal */
export const THEME_METRO: DemoOrgTheme = {
  primaryColor: '#0f766e',
  secondaryColor: '#ccfbf1',
  logoUrl: '/brand/logo-sm.svg',
  faviconUrl: null,
  fontFamily: '"Montserrat", "Segoe UI", system-ui, -apple-system, sans-serif',
  mode: 'light',
  primaryShade: 6,
  defaultRadius: 'md',
  other: {
    vipsoNavy: '#042f2e',
    vipsoCanvas: '#f0fdfa',
    vipsoRed: '#0f766e',
    vipsoSecondary: '#ccfbf1',
    brandLabel: 'Metro Distributors',
  },
};

export const DEMO_CONFIG = {
  defaultContextLogin: false,
  loginType: 'OTP' as const,
  logoUrl: '/brand/logo-sm.svg',
  maxOrgs: 10,
  maxUsers: 50,
  enabledModules: null as string[] | null,
};

export type DemoOrg = {
  id: string;
  orgId: string;
  name: string;
  uniqueId: string;
  status: string;
  membershipId: string;
  timezone: string;
  phone: string;
  tenantId: string;
  /** Same document shape as real `initApp.theme` / tenant theme JSON. */
  theme: DemoOrgTheme;
};

/** Multiple orgs so the org picker is exercised in the demo. */
export const DEMO_ORGS: DemoOrg[] = [
  {
    id: '101',
    orgId: '101',
    name: 'Acme Traders',
    uniqueId: 'ACME',
    status: 'ACTIVE',
    membershipId: '1001',
    timezone: 'Asia/Kolkata',
    phone: '9876543210',
    tenantId: '1',
    theme: THEME_ACME,
  },
  {
    id: '102',
    orgId: '102',
    name: 'Sunrise Wholesale',
    uniqueId: 'SUNRISE',
    status: 'ACTIVE',
    membershipId: '1002',
    timezone: 'Asia/Kolkata',
    phone: '9876543211',
    tenantId: '1',
    theme: THEME_SUNRISE,
  },
  {
    id: '103',
    orgId: '103',
    name: 'Metro Distributors',
    uniqueId: 'METRO',
    status: 'ACTIVE',
    membershipId: '1003',
    timezone: 'Asia/Kolkata',
    phone: '9876543212',
    tenantId: '1',
    theme: THEME_METRO,
  },
];

export const DEMO_USER = {
  id: '501',
  name: 'Demo User',
  phone: '9999999999',
  email: 'demo@vipso.app',
};

export const DEMO_FEATURE_KEYS = [
  'customers',
  'vendors',
  'products',
  'procurement',
  'staff',
  'warehouse',
  'transport',
  'labour',
  'tasks',
  'settings',
  'billing.view',
];

const addr = (
  id: string,
  line1: string,
  city: string,
  state = 'Gujarat',
): NonNullable<Customer['address']> => ({
  id,
  line1,
  line2: null,
  city,
  state,
  postalCode: '380001',
  country: 'IN',
});

export const MOCK_CUSTOMERS: Customer[] = [
  { id: 'c1', name: 'Rajesh Hardware', phone: '9825011111', email: 'rajesh@example.com', openingBalance: 12500, address: addr('a1', '12 Ring Road', 'Ahmedabad'), approvalStatus: 'APPROVED' },
  { id: 'c2', name: 'Shree Cement Depot', phone: '9825022222', email: 'shree@example.com', openingBalance: 8400, address: addr('a2', 'Near ST Stand', 'Surat'), approvalStatus: 'APPROVED' },
  { id: 'c3', name: 'Patel Builders', phone: '9825033333', email: 'patel@example.com', openingBalance: 0, address: addr('a3', 'CG Road', 'Ahmedabad'), approvalStatus: 'PENDING' },
  { id: 'c4', name: 'Mehta Steels', phone: '9825044444', email: 'mehta@example.com', openingBalance: 22000, address: addr('a4', 'GIDC Estate', 'Vadodara'), approvalStatus: 'APPROVED' },
  { id: 'c5', name: 'Kiran Paints', phone: '9825055555', email: null, openingBalance: 3500, address: addr('a5', 'Station Road', 'Rajkot'), approvalStatus: 'REJECTED' },
  { id: 'c6', name: 'Om Electricals', phone: '9825066666', email: 'om@example.com', openingBalance: 1100, address: addr('a6', 'Ashram Road', 'Ahmedabad'), approvalStatus: 'APPROVED' },
  { id: 'c7', name: 'Galaxy Tiles', phone: '9825077777', email: 'galaxy@example.com', openingBalance: 9800, address: addr('a7', 'Katargam', 'Surat'), approvalStatus: 'APPROVED' },
  { id: 'c8', name: 'Narmada Pipes', phone: '9825088888', email: null, openingBalance: 4500, address: addr('a8', 'Old City', 'Bharuch'), approvalStatus: 'PENDING' },
  { id: 'c9', name: 'Urban Interiors', phone: '9825099999', email: 'urban@example.com', openingBalance: 15600, address: addr('a9', 'SG Highway', 'Ahmedabad'), approvalStatus: 'APPROVED' },
  { id: 'c10', name: 'Green Farm Co-op', phone: '9825000001', email: 'green@example.com', openingBalance: 2700, address: addr('a10', 'Market Yard', 'Anand'), approvalStatus: 'DRAFT' },
];

export const MOCK_VENDORS: Vendor[] = [
  { id: 'v1', name: 'UltraTech Cement Ltd', phone: '9123411111', email: 'sales@ultratech.example', openingBalance: 50000, address: addr('va1', 'Industrial Area', 'Mumbai', 'Maharashtra'), approvalStatus: 'APPROVED' },
  { id: 'v2', name: 'Tata Steel Supply', phone: '9123422222', email: 'orders@tata.example', openingBalance: 32000, address: addr('va2', 'Steel Hub', 'Jamshedpur', 'Jharkhand'), approvalStatus: 'APPROVED' },
  { id: 'v3', name: 'Asian Paints Dist.', phone: '9123433333', email: 'ap@example.com', openingBalance: 18000, address: addr('va3', 'Paint Market', 'Mumbai', 'Maharashtra'), approvalStatus: 'PENDING' },
  { id: 'v4', name: 'Gujarat Sand Co.', phone: '9123444444', email: null, openingBalance: 9000, address: addr('va4', 'River Bank', 'Bharuch'), approvalStatus: 'APPROVED' },
  { id: 'v5', name: 'Royal Aggregates', phone: '9123455555', email: 'royal@example.com', openingBalance: 14500, address: addr('va5', 'Quarry Road', 'Vadodara'), approvalStatus: 'APPROVED' },
  { id: 'v6', name: 'Supreme Pipes', phone: '9123466666', email: 'supreme@example.com', openingBalance: 7600, address: addr('va6', 'GIDC', 'Ankleshwar'), approvalStatus: 'REJECTED' },
  { id: 'v7', name: 'Orient Electric', phone: '9123477777', email: 'orient@example.com', openingBalance: 21000, address: addr('va7', 'Electronics Zone', 'Noida', 'UP'), approvalStatus: 'APPROVED' },
  { id: 'v8', name: 'Local Timber Yard', phone: '9123488888', email: null, openingBalance: 4200, address: addr('va8', 'Saw Mill Lane', 'Surat'), approvalStatus: 'DRAFT' },
];

export const MOCK_CATEGORIES: CategoryRow[] = [
  { id: 'cat1', name: 'Cement' },
  { id: 'cat2', name: 'Steel' },
  { id: 'cat3', name: 'Paints' },
  { id: 'cat4', name: 'Pipes' },
  { id: 'cat5', name: 'Electrical' },
  { id: 'cat6', name: 'Tiles' },
];

export const MOCK_UNITS: UnitRow[] = [
  { id: 'u1', name: 'Bag' },
  { id: 'u2', name: 'Kg' },
  { id: 'u3', name: 'Litre' },
  { id: 'u4', name: 'Piece' },
  { id: 'u5', name: 'Ton' },
  { id: 'u6', name: 'Meter' },
];

export const MOCK_PRODUCTS: Product[] = [
  { id: 'p1', name: 'OPC 53 Grade Cement', unit: 'Bag', categoryId: 'cat1', category: MOCK_CATEGORIES[0], quantity: 500, pricePerQuantity: 380, showSubse: true, addOnItem: false, description: 'Premium OPC cement', approvalStatus: 'APPROVED' },
  { id: 'p2', name: 'PPC Cement', unit: 'Bag', categoryId: 'cat1', category: MOCK_CATEGORIES[0], quantity: 320, pricePerQuantity: 340, showSubse: true, addOnItem: false, approvalStatus: 'APPROVED' },
  { id: 'p3', name: 'TMT Bar 12mm', unit: 'Kg', categoryId: 'cat2', category: MOCK_CATEGORIES[1], quantity: 2000, pricePerQuantity: 62, showSubse: true, addOnItem: false, approvalStatus: 'PENDING' },
  { id: 'p4', name: 'TMT Bar 16mm', unit: 'Kg', categoryId: 'cat2', category: MOCK_CATEGORIES[1], quantity: 1500, pricePerQuantity: 64, showSubse: true, addOnItem: false, approvalStatus: 'APPROVED' },
  { id: 'p5', name: 'Emulsion Paint White', unit: 'Litre', categoryId: 'cat3', category: MOCK_CATEGORIES[2], quantity: 180, pricePerQuantity: 220, showSubse: false, addOnItem: true, addOnItemName: 'Buckets', approvalStatus: 'APPROVED' },
  { id: 'p6', name: 'Exterior Primer', unit: 'Litre', categoryId: 'cat3', category: MOCK_CATEGORIES[2], quantity: 90, pricePerQuantity: 185, showSubse: false, addOnItem: false, approvalStatus: 'REJECTED' },
  { id: 'p7', name: 'PVC Pipe 4 inch', unit: 'Meter', categoryId: 'cat4', category: MOCK_CATEGORIES[3], quantity: 600, pricePerQuantity: 95, showSubse: true, addOnItem: false, approvalStatus: 'APPROVED' },
  { id: 'p8', name: 'CPVC Pipe 1 inch', unit: 'Meter', categoryId: 'cat4', category: MOCK_CATEGORIES[3], quantity: 400, pricePerQuantity: 72, showSubse: true, addOnItem: false, approvalStatus: 'DRAFT' },
  { id: 'p9', name: 'LED Bulb 9W', unit: 'Piece', categoryId: 'cat5', category: MOCK_CATEGORIES[4], quantity: 1000, pricePerQuantity: 85, showSubse: false, addOnItem: false, approvalStatus: 'APPROVED' },
  { id: 'p10', name: 'MCB 32A', unit: 'Piece', categoryId: 'cat5', category: MOCK_CATEGORIES[4], quantity: 250, pricePerQuantity: 310, showSubse: false, addOnItem: false, approvalStatus: 'PENDING' },
  { id: 'p11', name: 'Vitrified Tile 2x2', unit: 'Piece', categoryId: 'cat6', category: MOCK_CATEGORIES[5], quantity: 800, pricePerQuantity: 45, showSubse: true, addOnItem: true, addOnItemName: 'Boxes', approvalStatus: 'APPROVED' },
  { id: 'p12', name: 'Wall Tile Glossy', unit: 'Piece', categoryId: 'cat6', category: MOCK_CATEGORIES[5], quantity: 1200, pricePerQuantity: 28, showSubse: true, addOnItem: false, approvalStatus: 'APPROVED' },
  { id: 'p13', name: 'Binding Wire', unit: 'Kg', categoryId: 'cat2', category: MOCK_CATEGORIES[1], quantity: 350, pricePerQuantity: 72, showSubse: false, addOnItem: false, approvalStatus: 'APPROVED' },
  { id: 'p14', name: 'River Sand', unit: 'Ton', categoryId: 'cat1', category: MOCK_CATEGORIES[0], quantity: 40, pricePerQuantity: 1200, showSubse: false, addOnItem: false, approvalStatus: 'PENDING' },
  { id: 'p15', name: 'Crushed Aggregate 20mm', unit: 'Ton', categoryId: 'cat1', category: MOCK_CATEGORIES[0], quantity: 55, pricePerQuantity: 980, showSubse: false, addOnItem: false, approvalStatus: 'APPROVED' },
];

export const MOCK_CHAMBERS: ChamberRow[] = [
  { id: 'ch1', name: 'Chamber A' },
  { id: 'ch2', name: 'Chamber B' },
  { id: 'ch3', name: 'Chamber C' },
];

export const MOCK_FLOORS: FloorRow[] = [
  { id: 'fl1', name: 'Floor 1', chamberId: 'ch1' },
  { id: 'fl2', name: 'Floor 2', chamberId: 'ch1' },
  { id: 'fl3', name: 'Floor 1', chamberId: 'ch2' },
  { id: 'fl4', name: 'Floor 2', chamberId: 'ch2' },
  { id: 'fl5', name: 'Floor 1', chamberId: 'ch3' },
  { id: 'fl6', name: 'Floor 2', chamberId: 'ch3' },
];

export const MOCK_RACKS: RackRow[] = [
  { id: 'rk1', name: 'Rack 1', floorId: 'fl1' },
  { id: 'rk2', name: 'Rack 2', floorId: 'fl1' },
  { id: 'rk3', name: 'Rack 1', floorId: 'fl2' },
  { id: 'rk4', name: 'Rack 2', floorId: 'fl2' },
  { id: 'rk5', name: 'Rack 1', floorId: 'fl3' },
  { id: 'rk6', name: 'Rack 2', floorId: 'fl3' },
  { id: 'rk7', name: 'Rack 1', floorId: 'fl4' },
  { id: 'rk8', name: 'Rack 2', floorId: 'fl4' },
  { id: 'rk9', name: 'Rack 1', floorId: 'fl5' },
  { id: 'rk10', name: 'Rack 2', floorId: 'fl5' },
  { id: 'rk11', name: 'Rack 1', floorId: 'fl6' },
  { id: 'rk12', name: 'Rack 2', floorId: 'fl6' },
];

export type InventoryTransferRow = {
  id: string;
  orgId: string;
  productId: string;
  fromType: 'PURCHASE_POOL' | 'RACK';
  fromRackId: string | null;
  toType: 'RACK' | 'ALLOCATION_POOL';
  toRackId: string | null;
  warehouseId: string;
  quantity: number;
  notes: string | null;
  status: 'DRAFT' | 'CONFIRMED';
  createdAt: string;
  product: { id: string; name: string; unit?: string | null };
  fromRack: { id: string; name: string } | null;
  toRack: { id: string; name: string } | null;
};

export const MOCK_INVENTORY_TRANSFERS: InventoryTransferRow[] = [
  {
    id: 'it1',
    orgId: '101',
    productId: 'p1',
    fromType: 'PURCHASE_POOL',
    fromRackId: null,
    toType: 'RACK',
    toRackId: 'rk1',
    warehouseId: 'w1',
    quantity: 100,
    notes: 'Initial allocation from purchase pool',
    status: 'CONFIRMED',
    createdAt: '2026-07-28T10:00:00.000Z',
    product: { id: 'p1', name: 'OPC 53 Grade Cement', unit: 'Bag' },
    fromRack: null,
    toRack: { id: 'rk1', name: 'Rack 1' },
  },
  {
    id: 'it2',
    orgId: '101',
    productId: 'p3',
    fromType: 'RACK',
    fromRackId: 'rk1',
    toType: 'RACK',
    toRackId: 'rk5',
    warehouseId: 'w1',
    quantity: 500,
    notes: 'Rack rebalance — move steel bars to chamber B',
    status: 'DRAFT',
    createdAt: '2026-07-27T14:00:00.000Z',
    product: { id: 'p3', name: 'TMT Bar 12mm', unit: 'Kg' },
    fromRack: { id: 'rk1', name: 'Rack 1' },
    toRack: { id: 'rk5', name: 'Rack 1' },
  },
  {
    id: 'it3',
    orgId: '101',
    productId: 'p5',
    fromType: 'PURCHASE_POOL',
    fromRackId: null,
    toType: 'RACK',
    toRackId: 'rk9',
    warehouseId: 'w2',
    quantity: 60,
    notes: null,
    status: 'CONFIRMED',
    createdAt: '2026-07-26T09:30:00.000Z',
    product: { id: 'p5', name: 'Emulsion Paint White', unit: 'Litre' },
    fromRack: null,
    toRack: { id: 'rk9', name: 'Rack 1' },
  },
  {
    id: 'it4',
    orgId: '101',
    productId: 'p9',
    fromType: 'PURCHASE_POOL',
    fromRackId: null,
    toType: 'RACK',
    toRackId: 'rk3',
    warehouseId: 'w1',
    quantity: 200,
    notes: 'LED bulbs to rack 3',
    status: 'DRAFT',
    createdAt: '2026-07-25T16:00:00.000Z',
    product: { id: 'p9', name: 'LED Bulb 9W', unit: 'Piece' },
    fromRack: null,
    toRack: { id: 'rk3', name: 'Rack 1' },
  },
];

function locRefs(chamberId?: string, floorId?: string, rackId?: string) {
  const chamber = chamberId
    ? MOCK_CHAMBERS.find((c) => c.id === chamberId) ?? null
    : null;
  const floor = floorId ? MOCK_FLOORS.find((f) => f.id === floorId) ?? null : null;
  const rack = rackId ? MOCK_RACKS.find((r) => r.id === rackId) ?? null : null;
  return {
    chamberId: chamberId ?? null,
    floorId: floorId ?? null,
    rackId: rackId ?? null,
    chamber: chamber ? { id: chamber.id, name: chamber.name } : null,
    floor: floor ? { id: floor.id, name: floor.name } : null,
    rack: rack ? { id: rack.id, name: rack.name } : null,
  };
}

function procLine(
  id: string,
  product: Product,
  qty: number,
  price: number,
  extras?: {
    chamberId?: string;
    floorId?: string;
    rackId?: string;
    lossPercent?: number;
    lossKg?: number;
  },
): ProcProductLine {
  return {
    id,
    quantity: qty,
    productId: product.id,
    pricePerUnit: price,
    productPrice: price * qty,
    qtyUnit: product.unit,
    product: {
      id: product.id,
      name: product.name,
      unit: product.unit,
      showSubse: product.showSubse,
      addOnItem: product.addOnItem,
      addOnItemName: product.addOnItemName,
    },
    ...locRefs(extras?.chamberId, extras?.floorId, extras?.rackId),
    lossPercent: extras?.lossPercent ?? 0,
    lossKg: extras?.lossKg ?? 0,
  };
}

export const MOCK_WAREHOUSES: ContactRow[] = [
  { id: 'w1', name: 'Main Godown', phone: '9797011111', email: 'godown@acme.example' },
  { id: 'w2', name: 'City Depot', phone: '9797022222', email: null },
  { id: 'w3', name: 'Highway Yard', phone: '9797033333', email: 'yard@acme.example' },
];

export const MOCK_TRANSPORT: ContactRow[] = [
  { id: 't1', name: 'Shakti Logistics', phone: '9788011111' },
  { id: 't2', name: 'Swift Cargo', phone: '9788022222' },
  { id: 't3', name: 'Local Tempo Service', phone: '9788033333' },
];

export const MOCK_LABOUR: ContactRow[] = [
  { id: 'l1', name: 'Ramesh Loading Team', phone: '9777011111' },
  { id: 'l2', name: 'Suresh Contractors', phone: '9777022222' },
  { id: 'l3', name: 'Daily Wage Pool', phone: '9777033333' },
];

export const MOCK_SALES: ProcRow[] = [
  {
    id: 's1', procType: 'SALES', ordNumber: 'SO-1001', procAmount: 45600, customerId: 'c1', warehouseId: 'w1',
    createdAt: '2026-07-28T10:00:00.000Z', taxAmount: 3600, customer: MOCK_CUSTOMERS[0],
    labour: MOCK_LABOUR[0], transport: MOCK_TRANSPORT[0], vehicleNo: 'GJ01AB1234',
    receiptNumber: 'B12-L03', gatePassNumber: 'GP-7781', loadNumber: 'L03', bagNumber: 'B12',
    approvalStatus: 'APPROVED', approvedById: '501', approvedAt: '2026-07-28T12:00:00.000Z',
    approvedBy: { id: '501', name: 'Demo User' },
    products: [
      procLine('sl1', MOCK_PRODUCTS[0], 100, 380, { chamberId: 'ch1', floorId: 'fl1', rackId: 'rk1', lossPercent: 1.5, lossKg: 2 }),
      procLine('sl2', MOCK_PRODUCTS[2], 50, 62, { chamberId: 'ch1', floorId: 'fl1', rackId: 'rk2', lossPercent: 0.5, lossKg: 0.25 }),
    ],
  },
  {
    id: 's2', procType: 'SALES', ordNumber: 'SO-1002', procAmount: 18200, customerId: 'c2', warehouseId: 'w1',
    createdAt: '2026-07-27T11:30:00.000Z', customer: MOCK_CUSTOMERS[1],
    receiptNumber: 'B08-L01', gatePassNumber: 'GP-7782', loadNumber: 'L01', bagNumber: 'B08',
    approvalStatus: 'PENDING',
    products: [procLine('sl3', MOCK_PRODUCTS[4], 40, 220, { chamberId: 'ch2', floorId: 'fl3', rackId: 'rk5', lossPercent: 2, lossKg: 1 })],
  },
  {
    id: 's3', procType: 'SALES', ordNumber: 'SO-1003', procAmount: 31400, customerId: 'c3', warehouseId: 'w2',
    createdAt: '2026-07-26T09:15:00.000Z', customer: MOCK_CUSTOMERS[2],
    receiptNumber: 'B15-L05', loadNumber: 'L05', bagNumber: 'B15',
    approvalStatus: 'REJECTED',
    products: [procLine('sl4', MOCK_PRODUCTS[10], 200, 45, { chamberId: 'ch3', floorId: 'fl5', rackId: 'rk9', lossPercent: 0, lossKg: 0 })],
  },
  {
    id: 's4', procType: 'SALES', ordNumber: 'SO-1004', procAmount: 9800, customerId: 'c5', warehouseId: 'w1',
    createdAt: '2026-07-25T14:00:00.000Z', customer: MOCK_CUSTOMERS[4],
    products: [procLine('sl5', MOCK_PRODUCTS[8], 100, 85)],
  },
  {
    id: 's5', procType: 'SALES', ordNumber: 'SO-1005', procAmount: 67200, customerId: 'c4', warehouseId: 'w3',
    createdAt: '2026-07-24T08:45:00.000Z', customer: MOCK_CUSTOMERS[3], transport: MOCK_TRANSPORT[1],
    receiptNumber: 'B20-L09', gatePassNumber: 'GP-7790', loadNumber: 'L09', bagNumber: 'B20',
    approvalStatus: 'APPROVED', approvedById: '502', approvedAt: '2026-07-24T10:00:00.000Z',
    approvedBy: { id: '502', name: 'Priya Shah' },
    products: [procLine('sl6', MOCK_PRODUCTS[3], 800, 64, { chamberId: 'ch2', floorId: 'fl4', rackId: 'rk7', lossPercent: 1, lossKg: 8 })],
  },
  {
    id: 's6', procType: 'SALES', ordNumber: 'SO-1006', procAmount: 12400, customerId: 'c6', warehouseId: 'w2',
    createdAt: '2026-07-23T16:20:00.000Z', customer: MOCK_CUSTOMERS[5],
    products: [procLine('sl7', MOCK_PRODUCTS[9], 40, 310)],
  },
  {
    id: 's7', procType: 'SALES', ordNumber: 'SO-1007', procAmount: 22800, customerId: 'c7', warehouseId: 'w1',
    createdAt: '2026-07-22T12:00:00.000Z', customer: MOCK_CUSTOMERS[6],
    products: [procLine('sl8', MOCK_PRODUCTS[11], 400, 28)],
  },
  {
    id: 's8', procType: 'SALES', ordNumber: 'SO-1008', procAmount: 15600, customerId: 'c9', warehouseId: 'w2',
    createdAt: '2026-07-21T10:30:00.000Z', customer: MOCK_CUSTOMERS[8],
    products: [procLine('sl9', MOCK_PRODUCTS[5], 60, 185)],
  },
  {
    id: 's9', procType: 'SALES', ordNumber: 'SO-1009', procAmount: 40800, customerId: 'c1', warehouseId: 'w1',
    createdAt: '2026-07-20T09:00:00.000Z', customer: MOCK_CUSTOMERS[0],
    receiptNumber: 'B04-L02', gatePassNumber: 'GP-7701', loadNumber: 'L02', bagNumber: 'B04',
    approvalStatus: 'PENDING',
    products: [procLine('sl10', MOCK_PRODUCTS[1], 120, 340, { chamberId: 'ch1', floorId: 'fl2', rackId: 'rk3', lossPercent: 0.8, lossKg: 1.2 })],
  },
  {
    id: 's10', procType: 'SALES', ordNumber: 'SO-1010', procAmount: 8900, customerId: 'c10', warehouseId: 'w3',
    createdAt: '2026-07-19T15:40:00.000Z', customer: MOCK_CUSTOMERS[9],
    products: [procLine('sl11', MOCK_PRODUCTS[6], 80, 95)],
  },
];

export const MOCK_PURCHASES: ProcRow[] = [
  {
    id: 'pu1', procType: 'PURCHASE', ordNumber: 'PO-2001', procAmount: 190000, vendorId: 'v1', warehouseId: 'w1',
    createdAt: '2026-07-28T08:00:00.000Z', vendor: MOCK_VENDORS[0], biltyNo: 'BLT-991',
    receiptNumber: 'B01-L10', loadNumber: 'L10', bagNumber: 'B01',
    products: [procLine('pl1', MOCK_PRODUCTS[0], 500, 380, { chamberId: 'ch1', floorId: 'fl1', rackId: 'rk1', lossPercent: 0.2, lossKg: 1 })],
  },
  {
    id: 'pu2', procType: 'PURCHASE', ordNumber: 'PO-2002', procAmount: 124000, vendorId: 'v2', warehouseId: 'w1',
    createdAt: '2026-07-27T09:30:00.000Z', vendor: MOCK_VENDORS[1],
    products: [procLine('pl2', MOCK_PRODUCTS[2], 2000, 62, { chamberId: 'ch2', floorId: 'fl3', rackId: 'rk5', lossPercent: 0.5, lossKg: 10 })],
  },
  {
    id: 'pu3', procType: 'PURCHASE', ordNumber: 'PO-2003', procAmount: 39600, vendorId: 'v3', warehouseId: 'w2',
    createdAt: '2026-07-26T11:00:00.000Z', vendor: MOCK_VENDORS[2],
    products: [procLine('pl3', MOCK_PRODUCTS[4], 180, 220)],
  },
  {
    id: 'pu4', procType: 'PURCHASE', ordNumber: 'PO-2004', procAmount: 48000, vendorId: 'v4', warehouseId: 'w3',
    createdAt: '2026-07-25T07:45:00.000Z', vendor: MOCK_VENDORS[3],
    products: [procLine('pl4', MOCK_PRODUCTS[13], 40, 1200)],
  },
  {
    id: 'pu5', procType: 'PURCHASE', ordNumber: 'PO-2005', procAmount: 53900, vendorId: 'v5', warehouseId: 'w3',
    createdAt: '2026-07-24T13:20:00.000Z', vendor: MOCK_VENDORS[4],
    products: [procLine('pl5', MOCK_PRODUCTS[14], 55, 980)],
  },
  {
    id: 'pu6', procType: 'PURCHASE', ordNumber: 'PO-2006', procAmount: 28800, vendorId: 'v6', warehouseId: 'w2',
    createdAt: '2026-07-23T10:10:00.000Z', vendor: MOCK_VENDORS[5],
    products: [procLine('pl6', MOCK_PRODUCTS[7], 400, 72)],
  },
  {
    id: 'pu7', procType: 'PURCHASE', ordNumber: 'PO-2007', procAmount: 77500, vendorId: 'v7', warehouseId: 'w1',
    createdAt: '2026-07-22T16:00:00.000Z', vendor: MOCK_VENDORS[6],
    products: [procLine('pl7', MOCK_PRODUCTS[8], 500, 85), procLine('pl8', MOCK_PRODUCTS[9], 100, 310)],
  },
  {
    id: 'pu8', procType: 'PURCHASE', ordNumber: 'PO-2008', procAmount: 16800, vendorId: 'v8', warehouseId: 'w2',
    createdAt: '2026-07-21T12:30:00.000Z', vendor: MOCK_VENDORS[7],
    products: [procLine('pl9', MOCK_PRODUCTS[12], 200, 72)],
  },
  {
    id: 'pu9', procType: 'PURCHASE', ordNumber: 'PO-2009', procAmount: 108800, vendorId: 'v1', warehouseId: 'w1',
    createdAt: '2026-07-20T08:20:00.000Z', vendor: MOCK_VENDORS[0],
    products: [procLine('pl10', MOCK_PRODUCTS[1], 320, 340)],
  },
  {
    id: 'pu10', procType: 'PURCHASE', ordNumber: 'PO-2010', procAmount: 96000, vendorId: 'v2', warehouseId: 'w1',
    createdAt: '2026-07-19T14:50:00.000Z', vendor: MOCK_VENDORS[1],
    products: [procLine('pl11', MOCK_PRODUCTS[3], 1500, 64)],
  },
];

export const MOCK_PROCUREMENT: ProcRow[] = [...MOCK_SALES, ...MOCK_PURCHASES];

export const MOCK_ROLES: RoleRow[] = [
  { id: 'r1', name: 'Admin' },
  { id: 'r2', name: 'Manager' },
  { id: 'r3', name: 'Accountant' },
  { id: 'r4', name: 'Sales' },
];

export type MockApprovalLevel = {
  id: string;
  levelOrder: number;
  roleId: string;
  roleName: string;
  minAmount: number;
};

export type MockApprovalDefinition = {
  id: string;
  orgId: string;
  moduleKey: string;
  isEnabled: boolean;
  levels: MockApprovalLevel[];
};

/** Mutable org patterns — Sales shows amount-based multi-level example. */
export const MOCK_APPROVAL_DEFINITIONS: MockApprovalDefinition[] = [
  { id: 'ad-customer', orgId: '101', moduleKey: 'CUSTOMER', isEnabled: false, levels: [] },
  { id: 'ad-vendor', orgId: '101', moduleKey: 'VENDOR', isEnabled: false, levels: [] },
  { id: 'ad-product', orgId: '101', moduleKey: 'PRODUCT', isEnabled: false, levels: [] },
  { id: 'ad-warehouse', orgId: '101', moduleKey: 'WAREHOUSE', isEnabled: false, levels: [] },
  {
    id: 'ad-sales',
    orgId: '101',
    moduleKey: 'PROC_SALES',
    isEnabled: true,
    levels: [
      { id: 'asl1', levelOrder: 1, roleId: 'r2', roleName: 'Manager', minAmount: 0 },
      { id: 'asl2', levelOrder: 2, roleId: 'r1', roleName: 'Admin', minAmount: 50000 },
      { id: 'asl3', levelOrder: 3, roleId: 'r1', roleName: 'Admin', minAmount: 200000 },
    ],
  },
  {
    id: 'ad-purchase',
    orgId: '101',
    moduleKey: 'PROC_PURCHASE',
    isEnabled: true,
    levels: [
      { id: 'apl1', levelOrder: 1, roleId: 'r3', roleName: 'Accountant', minAmount: 0 },
    ],
  },
];

export const MOCK_STAFF: StaffRow[] = [
  { id: 'st1', userId: 'u501', roleId: 'r1', isActive: true, user: { id: 'u501', name: 'Demo User', phone: '9999999999', email: 'demo@vipso.app' }, role: MOCK_ROLES[0] },
  { id: 'st2', userId: 'u502', roleId: 'r2', isActive: true, user: { id: 'u502', name: 'Priya Shah', phone: '9811111111', email: 'priya@example.com' }, role: MOCK_ROLES[1] },
  { id: 'st3', userId: 'u503', roleId: 'r3', isActive: true, user: { id: 'u503', name: 'Amit Desai', phone: '9822222222', email: 'amit@example.com' }, role: MOCK_ROLES[2] },
  { id: 'st4', userId: 'u504', roleId: 'r4', isActive: true, user: { id: 'u504', name: 'Neha Patel', phone: '9833333333', email: 'neha@example.com' }, role: MOCK_ROLES[3] },
  { id: 'st5', userId: 'u505', roleId: 'r4', isActive: false, user: { id: 'u505', name: 'Karan Mehta', phone: '9844444444' }, role: MOCK_ROLES[3] },
];

export const MOCK_TASKS: TaskRow[] = [
  {
    id: 'tk1',
    status: 'PENDING',
    priority: 'NORMAL',
    title: 'Complete organization profile',
    description: 'Add business email and contact phone for Acme Traders.',
    category: 'onboarding',
    template: { kind: 'FORM', priority: 'NORMAL', title: 'Complete organization profile' },
  },
  {
    id: 'tk2',
    status: 'PENDING',
    priority: 'CRITICAL',
    title: 'Verify warehouse address',
    description: 'Confirm the address for Main Godown.',
    category: 'compliance',
    template: { kind: 'FORM', priority: 'CRITICAL', title: 'Verify warehouse address' },
  },
  {
    id: 'tk3',
    status: 'PENDING',
    priority: 'HIGH',
    title: 'Approve sales order SO-1002',
    description: 'Review and approve sales order SO-1002 for Shree Cement Depot.',
    category: 'APPROVAL',
    subjectType: 'PROC',
    subjectId: 's2',
    template: {
      kind: 'APPROVAL',
      priority: 'HIGH',
      title: 'Approve sales order',
      actionKey: 'procurement.approve',
    },
  },
  {
    id: 'tk4',
    status: 'PENDING',
    priority: 'HIGH',
    title: 'Approve sales order SO-1009',
    description: 'Review and approve sales order SO-1009 for Rajesh Hardware.',
    category: 'APPROVAL',
    subjectType: 'PROC',
    subjectId: 's9',
    template: {
      kind: 'APPROVAL',
      priority: 'HIGH',
      title: 'Approve sales order',
      actionKey: 'procurement.approve',
    },
  },
];

export const MOCK_DASHBOARD_STATS: DashboardStats = {
  customers: MOCK_CUSTOMERS.length,
  vendors: MOCK_VENDORS.length,
  products: MOCK_PRODUCTS.length,
  salesOrders: MOCK_SALES.length,
  purchaseOrders: MOCK_PURCHASES.length,
  totalProcurement: MOCK_PROCUREMENT.length,
};

export function buildInitPayload(orgId: string, phone?: string) {
  const org = DEMO_ORGS.find((o) => o.id === orgId || o.orgId === orgId) ?? DEMO_ORGS[0];
  const theme = org.theme;
  // Fake JWT so clients can read membershipId the same way as production.
  const rawPayload = JSON.stringify({
    userId: 1,
    orgId: Number(org.orgId),
    membershipId: Number(org.membershipId),
    role: 'ORG_USER',
  });
  const jwtPayload =
    typeof btoa === 'function'
      ? btoa(rawPayload)
      : Buffer.from(rawPayload).toString('base64');
  const accessToken = `eyJhbGciOiJub25lIn0.${jwtPayload}.demo`;
  return {
    accessToken,
    refreshToken: 'demo-refresh-token',
    featureKeys: DEMO_FEATURE_KEYS,
    socketRooms: [] as string[],
    org: {
      id: org.id,
      orgId: org.orgId,
      name: org.name,
      status: org.status,
      phone: org.phone,
      uniqueId: org.uniqueId,
      tenantId: org.tenantId,
      timezone: org.timezone,
      logoUrl: theme.logoUrl ?? '/brand/logo-sm.svg',
    },
    tenant: DEMO_TENANT,
    // Same field as production `initApp.theme` — Mantine boots from this document.
    theme,
    user: {
      ...DEMO_USER,
      phone: phone || DEMO_USER.phone,
      name: phone ? `User ${phone.slice(-4)}` : DEMO_USER.name,
    },
    orgs: DEMO_ORGS.map((o) => ({
      orgId: o.orgId,
      name: o.name,
      membershipId: o.membershipId,
    })),
    membershipId: org.membershipId,
    pendingTaskCount: MOCK_TASKS.filter((t) => t.status === 'PENDING').length,
    operationsLocked: false,
  };
}

/** Lookup helper for UI (org picker swatches, settings). */
export function getOrgTheme(orgId: string): DemoOrgTheme {
  return (
    DEMO_ORGS.find((o) => o.id === orgId || o.orgId === orgId)?.theme ?? DEMO_THEME
  );
}
